import React, { FC } from 'react';
import { useLeasingCalculatorConfigurations } from 'src/app/product/hooks/useLeasingCalculatorConfigurations';
import { ProductSlider } from 'src/app/product/components/ProductSlider';
import { FinancingType, Offer } from 'src/common/models/offer';
import { Product } from 'src/app/common/models/product';
import styled, { css } from 'styled-components';
import { CustomerType } from 'src/common/models/customerType';
import { media, widthBreakpoints } from 'src/config/breakpoints';
import MediaQuery from 'react-responsive';
import { ProductPrice } from 'src/app/product/components/ProductPrice';
import { ProductPageSummaryListPrice } from 'src/app/product/components/ProductPageSummary/ProductPageSummaryListPrice';
import { ProductPageSummaryOfferOptions } from 'src/app/product/components/ProductPageSummary/ProductPageSummaryOfferOptions';
import { ProductPageSummaryOfferVariants } from 'src/app/product/components/ProductPageSummary/ProductPageSummaryOfferVariants';
import { financingTypeMapper } from 'src/app/product/services/offerMappers';
import { B2BB2CSwitch } from 'src/common/components/B2BB2CSwitch';
import { transparentize } from 'polished';
import { colors } from 'src/config/colors';
import { useSelector } from 'react-redux';
import { financingTypesListSelector } from 'src/app/product/state/products/productsSelectors';
import { customerTypeSelector } from 'src/app/state/customerType/customerTypeSelectors';
import { isAuthenticatedSelector } from 'src/state/auth/authSelectors';
import { localFormatNumber } from 'src/lib/localFormatNumber';
import { Link } from 'gatsby';
import { LoginRoute } from 'src/public/publicRoutes';

const STANDARD_LEASING_KEY = '__standard__';
const VAT_RATE = 1.23;

const applyVAT = (value: number, customerType: string) =>
  customerType === CustomerType.B2C ? Math.round(value * VAT_RATE) : value;

const Configuration = styled.div`
  margin-bottom: 1.25rem;

  ${media.w.min.px1200(css`
    margin-left: -0.25rem;
    margin-right: -0.25rem;
  `)}
`;

const StyledB2BB2CSwitch = styled(B2BB2CSwitch)`
  width: 100%;
  background: ${transparentize(0.9, colors.white)};
  border-color: transparent;
`;

const CrossedOutRate = styled.div`
  color: ${colors.white};
  font-size: 0.8rem;
  font-weight: bold;
  opacity: 0.6;
  text-decoration: line-through;
  margin-bottom: 0.25rem;
`;

const SavingsRow = styled.div`
  color: ${colors.white};
  font-size: 0.8rem;
  font-weight: bold;
  margin-top: 0.25rem;
`;

const SavingsAmount = styled.strong`
  color: #fcb103;
`;

const LoginPromptLink = styled(Link)`
  color: #fcb103;
  font-size: 0.8rem;
  font-weight: bold;
  margin-top: 0.25rem;
  display: block;
  text-decoration: underline;
`;

export interface LeasingCalculatorProps {
  activeOffer: Offer;
  leasingOffers: Offer[];
  product: Product;
  onFinancingTypeChange: (financingType: string[]) => void;
  activeLeasingLabel: string | null;
  onLeasingLabelChange: (label: string | null) => void;
  hasStandardLeasing: boolean;
  lockedLeasingLabels: string[];
}

export const LeasingCalculator: FC<LeasingCalculatorProps> = ({
  activeOffer,
  leasingOffers,
  product,
  onFinancingTypeChange,
  activeLeasingLabel,
  onLeasingLabelChange,
  hasStandardLeasing,
  lockedLeasingLabels,
}) => {
  const financingTypesList = useSelector(financingTypesListSelector);
  const userCustomerType = useSelector(customerTypeSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const isStandardLeasing = activeOffer?.external?.type === 'artefakta4internal';
  const isLockedLeasing =
    activeOffer?.financingType === FinancingType.Leasing && !!activeOffer?.leasingLabel;

  // Locked leasing: use global customerType for VAT display (brutto/netto calculated, no b2c record needed)
  const displayCustomerType = isLockedLeasing ? userCustomerType : activeOffer.customerType;

  const hasBenefit = isLockedLeasing || (isStandardLeasing && !!activeOffer?.estimatedSavingsNetto);

  const SURCHARGE_NETTO = 80;
  const surcharge = displayCustomerType === CustomerType.B2B
    ? SURCHARGE_NETTO
    : Math.round(SURCHARGE_NETTO * VAT_RATE);
  const basePrice = isLockedLeasing && displayCustomerType === CustomerType.B2C
    ? Math.round(activeOffer.price * VAT_RATE)
    : activeOffer.price;
  const crossedOutRate = basePrice + surcharge;
  const rateLabel = displayCustomerType === CustomerType.B2B ? 'netto' : 'brutto';

  const {
    leasingInitialPaymentsList,
    activeInitialPayment,
    setLeasingInitialPayment,
    leasingContractPeriodsList,
    activeContractPeriod,
    setLeasingContractPeriod,
    leasingBuyoutPriceList,
    activeBuyoutPrice,
    setLeasingBuyoutPrice,
  } = useLeasingCalculatorConfigurations(activeOffer, leasingOffers, product);

  if (!isStandardLeasing && !isLockedLeasing) {
    return <></>;
  }

  const showLeasingTypeToggle =
    (hasStandardLeasing && lockedLeasingLabels.length > 0) ||
    lockedLeasingLabels.length > 1;

  const leasingTypeItems = [
    ...(hasStandardLeasing ? [{ id: STANDARD_LEASING_KEY, label: 'Standardowy' }] : []),
    ...lockedLeasingLabels.map((l) => ({ id: l, label: l })),
  ];

  return (
    <>
      <Configuration>
        <StyledB2BB2CSwitch value={displayCustomerType} />

        {financingTypesList.length > 0 && (
          <ProductPageSummaryOfferVariants
            items={financingTypesList.map((id) => ({
              id,
              label: financingTypeMapper[id],
            }))}
            name="Forma finansowania"
            onChange={(a) => onFinancingTypeChange(a)}
            value={activeOffer.financingType}
          />
        )}

        {showLeasingTypeToggle && (
          <ProductPageSummaryOfferVariants
            items={leasingTypeItems}
            name="Typ leasingu"
            onChange={([v]) =>
              onLeasingLabelChange(v === STANDARD_LEASING_KEY ? null : v)
            }
            value={activeLeasingLabel ?? STANDARD_LEASING_KEY}
          />
        )}

        {leasingInitialPaymentsList.length > 0 && (
          <ProductSlider
            min={0}
            label="Wkład własny"
            step={1}
            suffix="zł"
            content={applyVAT(
              activeInitialPayment,
              displayCustomerType
            )}
            value={leasingInitialPaymentsList.findIndex(
              (payment) => payment === activeInitialPayment
            )}
            onChange={(_, v) =>
              typeof v === 'number' &&
              activeInitialPayment !== leasingInitialPaymentsList[v] &&
              setLeasingInitialPayment(leasingInitialPaymentsList[v])
            }
            max={leasingInitialPaymentsList.length - 1}
            disabled={leasingInitialPaymentsList.length === 1}
          />
        )}

        {leasingContractPeriodsList.length > 0 && (
          <ProductSlider
            min={0}
            label="Liczba rat"
            step={1}
            suffix="mc"
            content={activeContractPeriod}
            value={leasingContractPeriodsList.findIndex(
              (payment) => payment === activeContractPeriod
            )}
            onChange={(_, v) =>
              typeof v === 'number' &&
              activeContractPeriod !== leasingContractPeriodsList[v] &&
              setLeasingContractPeriod(leasingContractPeriodsList[v])
            }
            max={leasingContractPeriodsList.length - 1}
            disabled={leasingContractPeriodsList.length === 1}
          />
        )}

        {leasingBuyoutPriceList.length > 0 && (
          <ProductSlider
            min={0}
            label="Kwota wykupu"
            step={1}
            suffix="zł"
            content={applyVAT(
              activeBuyoutPrice,
              displayCustomerType
            )}
            value={leasingBuyoutPriceList.findIndex(
              (payment) => payment === activeBuyoutPrice
            )}
            onChange={(_, v) =>
              typeof v === 'number' &&
              activeBuyoutPrice !== leasingBuyoutPriceList[v] &&
              setLeasingBuyoutPrice(leasingBuyoutPriceList[v])
            }
            max={leasingBuyoutPriceList.length - 1}
            disabled={leasingBuyoutPriceList.length === 1}
          />
        )}

        <ProductPageSummaryOfferOptions
          financingType={activeOffer.financingType}
          productType={product.type}
          packageType={activeOffer.packageType}
        />
      </Configuration>

      {activeOffer && (
        <MediaQuery minWidth={widthBreakpoints.px1200}>
          <>
            {isAuthenticated && hasBenefit && (
              <CrossedOutRate>
                Rata: {localFormatNumber(crossedOutRate)} zł {rateLabel} /miesiąc *
              </CrossedOutRate>
            )}

            <ProductPrice offer={activeOffer} discountBadge />

            {product?.listPrice && (
              <ProductPageSummaryListPrice price={product.listPrice} />
            )}

            {isAuthenticated && hasBenefit && activeOffer.estimatedSavingsNetto && (
              <SavingsRow>
                Pakiet Korzyści:{' '}
                <SavingsAmount>
                  {displayCustomerType === CustomerType.B2B
                    ? `${localFormatNumber(activeOffer.estimatedSavingsNetto)} zł netto`
                    : `${localFormatNumber(Math.round(activeOffer.estimatedSavingsNetto * VAT_RATE))} zł brutto`}
                </SavingsAmount>
              </SavingsRow>
            )}

            {!isAuthenticated && hasBenefit && (
              <LoginPromptLink to={LoginRoute}>
                Zaloguj się i sprawdź swoje benefity
              </LoginPromptLink>
            )}
          </>
        </MediaQuery>
      )}
    </>
  );
};
